import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Empty,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import {
  LayersControl,
  MapContainer,
  Polyline,
  TileLayer,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";

const { Text } = Typography;

interface SourceRow {
  id: number;
  filename: string;
  size_bytes: number;
  uploaded_at: string | null;
  uploaded_by_name: string | null;
}

interface RouteDetail {
  id: number;
  code: string;
  route_date: string;
  distance: number | string | null;
  geometry_geojson: GeoJSON.LineString | null;
  gpx_file_url: string | null;
  sources: SourceRow[];
  auditor?: { id: number; name: string } | null;
}

interface Props {
  open: boolean;
  routeId: number | null;
  /** Para gating del botón "Resetear": admin → siempre; auditor →
   *  sólo si es el dueño de la ruta. Lo decidimos aquí leyendo
   *  `routeDetail.auditor.id` vs el `currentUserId`. */
  currentUserId: number | null;
  isAdmin: boolean;
  onClose: () => void;
  /** Llamado tras un reset exitoso para que el padre refresque la
   *  lista (el badge sources_count pasará a 0 y has_geometry a false). */
  onReset?: () => void;
}

const bboxOf = (
  geom: GeoJSON.LineString | null,
): [[number, number], [number, number]] | null => {
  if (!geom || !geom.coordinates?.length) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const [lon, lat] of geom.coordinates as number[][]) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  return [
    [minLat, minLon],
    [maxLat, maxLon],
  ];
};

/**
 * Modal que previsualiza una ruta recorrida en Leaflet + lista los
 * archivos originales que la componen + ofrece "Resetear ruta" (vacía
 * la geometría + ZIP + sources, mantiene la fila).
 *
 * Accesible desde la tabla de rutas en `/planning-groups/show/<id>`.
 */
export const RoutePreviewModal = ({
  open,
  routeId,
  currentUserId,
  isAdmin,
  onClose,
  onReset,
}: Props) => {
  const { t } = useTranslation();
  const { message } = AntdApp.useApp();
  const [route, setRoute] = useState<RouteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    if (!open || !routeId) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<RouteDetail>(`/routes/${routeId}/`)
      .then((resp) => {
        if (!cancelled) setRoute(resp.data);
      })
      .catch((err) => {
        if (!cancelled) {
          message.error(
            err?.response?.data?.detail ||
              err?.message ||
              t("route.preview_load_error", "No se pudo cargar la ruta"),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, routeId, message, t]);

  const positions = useMemo(() => {
    if (!route?.geometry_geojson) return [] as [number, number][];
    return (route.geometry_geojson.coordinates as number[][]).map(
      ([lon, lat]) => [lat, lon] as [number, number],
    );
  }, [route?.geometry_geojson]);

  const bounds = useMemo(
    () => bboxOf(route?.geometry_geojson ?? null),
    [route?.geometry_geojson],
  );

  const canReset = (() => {
    if (!route) return false;
    if (isAdmin) return true;
    const ownerId = route.auditor?.id ?? null;
    return !!currentUserId && ownerId === currentUserId;
  })();

  const handleReset = async () => {
    if (!route) return;
    setResetting(true);
    try {
      await apiClient.post(`/routes/${route.id}/reset/`);
      message.success(
        t("route.reset_success", "Ruta reseteada — ya puedes subir ficheros nuevos"),
      );
      // Refetch del detalle para que el modal muestre el estado vacío.
      const resp = await apiClient.get<RouteDetail>(`/routes/${route.id}/`);
      setRoute(resp.data);
      onReset?.();
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        t("route.reset_error", "No se pudo resetear la ruta");
      message.error(detail);
    } finally {
      setResetting(false);
    }
  };

  const distanceLabel = (() => {
    if (!route) return "—";
    const d = Number(route.distance);
    if (!Number.isFinite(d) || d <= 0) return "—";
    return `${d.toFixed(2)} km`;
  })();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={920}
      title={
        <Space>
          <EnvironmentOutlined />
          <span>{t("route.preview_title", "Recorrido GPS")}</span>
          {route?.code && (
            <Tag color="default" style={{ fontSize: 11 }}>
              {route.code}
            </Tag>
          )}
        </Space>
      }
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && route && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap size="middle">
            <Text>
              <strong>{t("route.route_date", "Fecha")}:</strong>{" "}
              {dayjs(route.route_date).format("DD/MM/YYYY")}
            </Text>
            <Text>
              <strong>{t("route.auditor", "Auditor")}:</strong>{" "}
              {route.auditor?.name ?? "—"}
            </Text>
            <Text>
              <strong>{t("route.distance", "Distancia")}:</strong>{" "}
              {distanceLabel}
            </Text>
            <Text>
              <strong>{t("route.files_count", "Archivos")}:</strong>{" "}
              {route.sources.length}
            </Text>
          </Space>

          {positions.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                t(
                  "route.preview_empty",
                  "Esta ruta no tiene geometría todavía (vacía o reseteada). Sube uno o más GPX/KML para generarla.",
                ) as string
              }
            />
          ) : (
            <div style={{ height: 360, width: "100%" }}>
              <MapContainer
                bounds={bounds ?? undefined}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom
              >
                <LayersControl position="topright">
                  <LayersControl.BaseLayer
                    checked
                    name={t("map.satellite_labels", "Satélite + etiquetas") as string}
                  >
                    {/* Pseudo-layer vacío que marca este BaseLayer como
                        activo; la combinación satélite + etiquetas se
                        pinta siempre con las Overlay de abajo cuando
                        este base está seleccionado. */}
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution="&copy; Esri · World Imagery + Reference overlay"
                      maxZoom={19}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer
                    name={t("map.satellite", "Satélite (sin etiquetas)") as string}
                  >
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                      attribution="&copy; Esri"
                      maxZoom={19}
                    />
                  </LayersControl.BaseLayer>
                  <LayersControl.BaseLayer
                    name={t("map.streets", "Calles") as string}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution="&copy; OpenStreetMap contributors"
                      maxZoom={19}
                    />
                  </LayersControl.BaseLayer>
                  {/* Overlays activas por defecto cuando el base es
                      "Satélite + etiquetas": transportes (carreteras y
                      vías) + boundaries y topónimos (fronteras, provs,
                      municipios, nombres de pueblo). Son tiles PNG con
                      transparencia servidas por Esri, encajan encima
                      del World_Imagery sin pisar el fondo. */}
                  <LayersControl.Overlay
                    checked
                    name={t("map.transport", "Carreteras") as string}
                  >
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}"
                      attribution="&copy; Esri · Transportation"
                      maxZoom={19}
                    />
                  </LayersControl.Overlay>
                  <LayersControl.Overlay
                    checked
                    name={t("map.labels", "Divisiones y topónimos") as string}
                  >
                    <TileLayer
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                      attribution="&copy; Esri · Boundaries & Places"
                      maxZoom={19}
                    />
                  </LayersControl.Overlay>
                </LayersControl>
                <Polyline
                  positions={positions}
                  pathOptions={{ color: "#1677ff", weight: 3 }}
                />
              </MapContainer>
            </div>
          )}

          {route.sources.length > 0 && (
            <div>
              <Text strong style={{ display: "block", marginBottom: 6 }}>
                {t("route.source_files", "Archivos que componen esta ruta")}
              </Text>
              <Table<SourceRow>
                size="small"
                rowKey="id"
                dataSource={route.sources}
                pagination={false}
                columns={[
                  {
                    title: t("route.filename", "Fichero"),
                    dataIndex: "filename",
                  },
                  {
                    title: t("common.size", "Tamaño"),
                    dataIndex: "size_bytes",
                    width: 110,
                    align: "right",
                    render: (b: number) =>
                      b > 1024 * 1024
                        ? `${(b / 1024 / 1024).toFixed(2)} MB`
                        : `${(b / 1024).toFixed(1)} KB`,
                  },
                  {
                    title: t("route.uploaded_at", "Subido"),
                    dataIndex: "uploaded_at",
                    width: 150,
                    render: (v: string | null) =>
                      v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—",
                  },
                  {
                    title: t("route.uploaded_by", "Por"),
                    dataIndex: "uploaded_by_name",
                    width: 160,
                    render: (v: string | null) => v ?? "—",
                  },
                ]}
              />
            </div>
          )}

          <Space wrap>
            {route.gpx_file_url && (
              <Button
                icon={<CloudDownloadOutlined />}
                href={route.gpx_file_url}
                target="_blank"
              >
                {t("route.download_zip", "Descargar ficheros (ZIP)")}
              </Button>
            )}
            {canReset && (
              <Popconfirm
                title={t("route.reset_confirm_title", "Resetear esta ruta")}
                description={
                  <div style={{ maxWidth: 320 }}>
                    {t(
                      "route.reset_confirm_body",
                      "Se borrarán la geometría, los archivos originales y el historial de uploads de esta ruta. La fila se mantiene — puedes volver a subir ficheros desde cero.",
                    )}
                  </div>
                }
                okText={t("common.confirm", "Confirmar")}
                okButtonProps={{ danger: true }}
                cancelText={t("common.cancel", "Cancelar")}
                onConfirm={handleReset}
              >
                <Button danger icon={<DeleteOutlined />} loading={resetting}>
                  {t("route.reset_btn", "Resetear ruta")}
                </Button>
              </Popconfirm>
            )}
          </Space>

          {!canReset && (
            <Alert
              type="info"
              showIcon
              style={{ fontSize: 12 }}
              message={t(
                "route.reset_readonly_hint",
                "Sólo el auditor dueño de la ruta o un administrador pueden resetearla.",
              )}
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default RoutePreviewModal;
